import React, { useState } from 'react';
import { Users, Building2, Globe, Scale, ShieldCheck, FileCheck, Languages, ArrowRight, ChevronDown, ChevronUp, HardHat, Factory, FireExtinguisher, Calculator, Landmark, SprayCan, UtensilsCrossed, MessageSquareText } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { mockData } from '../data/mock';

const iconMap = {
  Users,
  Building2,
  Globe,
  Scale,
  ShieldCheck,
  FileCheck,
  Languages,
  HardHat,
  Factory,
  FireExtinguisher,
  Calculator,
  Landmark,
  SprayCan,
  UtensilsCrossed
};

// Cuadro azul para pedir más información (canaliza todas las consultas al formulario)
const InfoCard = ({ onClick, className = '' }) => (
  <div className={`rounded-xl bg-gradient-to-br from-blue-600 to-blue-800 text-white p-6 md:p-8 flex flex-col sm:flex-row sm:items-center gap-5 shadow-lg ${className}`}>
    <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
      <MessageSquareText className="w-6 h-6 md:w-7 md:h-7" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-lg md:text-xl font-bold mb-1">¿Te interesa alguno de estos servicios?</p>
      <p className="text-sm md:text-base text-blue-100">Contanos qué necesita tu empresa y te asesoramos.</p>
    </div>
    <button
      type="button"
      onClick={onClick}
      data-testid="servicios-mas-info-btn"
      className="inline-flex items-center justify-center bg-white text-blue-700 hover:bg-blue-50 font-semibold rounded-lg px-5 py-3 text-sm md:text-base transition-colors flex-shrink-0"
    >
      Pedí más información
      <ArrowRight className="ml-2 w-4 h-4" />
    </button>
  </div>
);

const MOBILE_VISIBLE = 6;

export const Benefits = () => {
  const [showAll, setShowAll] = useState(false);
  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="benefits" className="py-11 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-10">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Servicios
          </h2>
          <p className="text-base sm:text-lg text-gray-600">
            Como socio de la UIQ, accedés a servicios exclusivos con asesoramiento gratuito y descuentos especiales diseñados para impulsar el crecimiento de tu empresa industrial.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-4 md:mb-12">
          {mockData.benefits.map((benefit, index) => {
            const Icon = iconMap[benefit.icon];
            const hiddenOnMobile = !showAll && index >= MOBILE_VISIBLE;
            return (
              <Card
                key={benefit.id}
                data-testid={`benefit-card-${benefit.id}`}
                className={`group hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-500 ${hiddenOnMobile ? 'hidden md:block' : ''}`}
              >
                <CardContent className="p-4 md:p-6 flex md:block items-start gap-4">
                  <div className="w-11 h-11 md:w-14 md:h-14 bg-blue-100 rounded-xl flex items-center justify-center md:mb-4 flex-shrink-0 group-hover:bg-blue-600 transition-colors duration-300">
                    <Icon className="w-6 h-6 md:w-7 md:h-7 text-blue-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base md:text-xl font-bold text-gray-900 mb-1 md:mb-3 group-hover:text-blue-600 transition-colors">
                      {benefit.title}
                    </h3>
                    <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          <InfoCard onClick={scrollToContact} className="hidden md:flex md:col-span-2" />
        </div>

        {/* Mobile toggle */}
        <div className="md:hidden mb-8">
          <Button
            variant="outline"
            data-testid="benefits-toggle-btn"
            onClick={() => setShowAll(!showAll)}
            className="w-full border-2 border-blue-600 text-blue-600 hover:bg-blue-50 font-semibold py-5"
          >
            {showAll ? 'Ver menos servicios' : `Ver todos los servicios (${mockData.benefits.length})`}
            {showAll ? <ChevronUp className="ml-2 w-4 h-4" /> : <ChevronDown className="ml-2 w-4 h-4" />}
          </Button>
          <InfoCard onClick={scrollToContact} className="mt-4" />
        </div>

        {/* About UIQ Section */}
        <div className="mt-8 md:mt-12 bg-gradient-to-br from-blue-50 to-white rounded-3xl p-6 sm:p-8 lg:p-12">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Sobre la UIQ</h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                {mockData.aboutUIQ.description}
              </p>
              <div className="space-y-4">
                <div className="border-l-4 border-blue-600 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">Nuestra misión</h4>
                  <p className="text-gray-600">{mockData.aboutUIQ.mission}</p>
                </div>
                <div className="border-l-4 border-blue-600 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">Nuestra visión</h4>
                  <p className="text-gray-600">{mockData.aboutUIQ.vision}</p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img
                src="/img/sobre-1.webp"
                alt="Evento UIQ"
                className="rounded-xl shadow-lg w-full h-36 sm:h-48 object-cover hover:scale-105 transition-transform duration-300"
                loading="lazy"
              />
              <img
                src="/img/nave-brugsa.webp"
                alt="Recorrida por la planta de Brugsa, empresa socia"
                className="rounded-xl shadow-lg w-full h-36 sm:h-48 object-cover mt-6 sm:mt-8 hover:scale-105 transition-transform duration-300"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
